import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BellRing, CalendarClock, CheckCheck, Info, LoaderCircle, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";

const LEVEL_UI = {
  urgent: { icon: TriangleAlert, cls: "bg-rose-50 text-rouge" },
  warning: { icon: BellRing, cls: "bg-amber-50 text-ambre" },
  info: { icon: Info, cls: "bg-bleu-light text-bleu" },
};

export default function Messages() {
  const [data, setData] = useState(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    const ids = data.notifications.filter((n) => !n.read).map((n) => n.id);
    if (!ids.length) return;
    try {
      await api.post("/notifications/read", { ids });
      await load();
      window.dispatchEvent(new Event("notifications-updated"));
      toast.success("Tous les messages sont marqués comme lus.");
    } catch {
      toast.error("Impossible de marquer les messages comme lus.");
    }
  };

  if (data === null)
    return <div className="flex justify-center py-24"><LoaderCircle className="h-7 w-7 animate-spin text-bleu" /></div>;
  if (data === false)
    return <p className="py-24 text-center text-sm text-slate-500">Impossible de charger vos messages.</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-messages">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Messages</h1>
          <p className="mt-1 text-sm text-slate-500">Vos rappels automatiques d'échéances (J-14, J-7, J-3) et notifications.</p>
        </div>
        {data.unread > 0 && (
          <button onClick={markAllRead} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu" data-testid="messages-mark-all-read">
            <CheckCheck className="h-3.5 w-3.5" /> Tout marquer comme lu ({data.unread})
          </button>
        )}
      </div>

      {data.notifications.length === 0 && (
        <div className="card-integra p-8 text-center" data-testid="messages-empty">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-bleu-light"><BellRing className="h-6 w-6 text-bleu" /></span>
          <p className="mt-4 text-sm font-bold text-slate-900">Aucun message pour le moment</p>
          <p className="mt-1 text-xs text-slate-500">
            Vos rappels apparaîtront ici automatiquement à l'approche de vos échéances.{" "}
            <Link to="/dashboard/echeances" className="font-semibold text-bleu underline">Gérer mes échéances</Link>
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {data.notifications.map((n, i) => {
          const ui = LEVEL_UI[n.level] || LEVEL_UI.info;
          const Icon = ui.icon;
          return (
            <motion.li
              key={n.id}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}
              className={`card-integra flex items-start gap-3.5 p-4 ${n.read ? "opacity-60" : ""}`}
              data-testid={`message-item-${n.level}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ui.cls}`}><Icon className="h-4.5 w-4.5" /></span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-900">
                  {n.title}
                  {!n.read && <span className="rounded-full bg-bleu px-2 py-0.5 text-[9px] font-bold uppercase text-white" data-testid="message-unread-badge">Nouveau</span>}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{n.message}</p>
                {n.date && (
                  <Link to="/dashboard/echeances" className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-bleu hover:underline">
                    <CalendarClock className="h-3 w-3" /> Voir mes échéances
                  </Link>
                )}
              </div>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
